import { GAME_COMPAT_HOSTS, SEARCH_ENGINES, SMART_DIRECT_HOSTS } from '../constants';
import type { BrowserSettings, InternalPage, RenderMode } from '../types';

const INTERNAL_PREFIX = 'websurf://';

export function isInternalUrl(value: string): boolean {
  return value.startsWith(INTERNAL_PREFIX);
}

export function internalPageFromUrl(value: string): InternalPage | undefined {
  if (!isInternalUrl(value)) return undefined;
  const key = value.replace(INTERNAL_PREFIX, '').split(/[/?#]/)[0] || 'home';
  if (['home', 'settings', 'history', 'bookmarks', 'extensions', 'passwords', 'sandbox', 'search'].includes(key)) return key as InternalPage;
  return 'home';
}

export function activeSearchTemplate(settings: BrowserSettings): string {
  if (settings.searchProvider === 'custom') return settings.searchEngine || SEARCH_ENGINES.google.template;
  return SEARCH_ENGINES[settings.searchProvider]?.template || settings.searchEngine || SEARCH_ENGINES.google.template;
}

export function normalizeInputToUrl(input: string, settings: BrowserSettings): string {
  const trimmed = input.trim();
  if (!trimmed) return settings.homepage;
  if (trimmed.startsWith(INTERNAL_PREFIX)) return trimmed;
  if (/^[a-z]+:\/\//i.test(trimmed)) return trimmed;
  if (/^[\w.-]+\.[a-z]{2,}([/:?#].*)?$/i.test(trimmed) || /^localhost(:\d+)?/i.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return activeSearchTemplate(settings).replace('%s', encodeURIComponent(trimmed));
}

export function toDisplayUrl(url: string): string {
  if (isInternalUrl(url)) return url;
  try {
    const parsed = new URL(url);
    return parsed.href.replace(/^https?:\/\//, '').replace(/\/$/, '');
  } catch {
    return url;
  }
}

export function proxiedUrl(url: string): string {
  return `/api/proxy?url=${encodeURIComponent(url)}`;
}

export function readableUrl(url: string): string {
  return `/api/readable?url=${encodeURIComponent(url)}`;
}

export function snapshotUrl(url: string): string {
  return `/api/snapshot?url=${encodeURIComponent(url)}`;
}

export function siteInfoUrl(url: string): string {
  return `/api/site-info?url=${encodeURIComponent(url)}`;
}

export function searchApiUrl(engine: string, query: string): string {
  return `/api/search?engine=${encodeURIComponent(engine)}&q=${encodeURIComponent(query)}`;
}

export function gameResolveUrl(url: string): string {
  return `/api/game-resolve?url=${encodeURIComponent(url)}`;
}

export function urlOrigin(url: string): string {
  if (isInternalUrl(url)) return 'WebSurf internal';
  try { return new URL(url).origin; } catch { return 'Unknown origin'; }
}

export function faviconFor(url: string): string | undefined {
  if (isInternalUrl(url)) return '/icons/mark-circle.png';
  try {
    const parsed = new URL(url);
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(parsed.hostname)}&sz=32`;
  } catch {
    return undefined;
  }
}

export function matchesPattern(url: string, pattern: string): boolean {
  if (pattern === '<all_urls>') return /^https?:/.test(url);
  const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
  return new RegExp(`^${escaped}$`).test(url);
}

export function hostnameFor(url: string): string {
  try { return new URL(url).hostname.toLowerCase(); } catch { return ''; }
}

export function pathnameFor(url: string): string {
  try { return new URL(url).pathname.toLowerCase(); } catch { return ''; }
}

const YOUTUBE_HOSTS = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com', 'music.youtube.com', 'youtu.be']);
const VIDEO_ID_RE = /^[A-Za-z0-9_-]{6,15}$/;

export function isYouTubeHost(url: string): boolean {
  return YOUTUBE_HOSTS.has(hostnameFor(url));
}

export function youtubeVideoId(url: string): string | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    if (host === 'youtu.be') {
      const id = parsed.pathname.slice(1).split('/')[0];
      return VIDEO_ID_RE.test(id) ? id : null;
    }
    if (!YOUTUBE_HOSTS.has(host)) return null;
    if (parsed.pathname === '/watch') {
      const v = parsed.searchParams.get('v') || '';
      return VIDEO_ID_RE.test(v) ? v : null;
    }
    const m = parsed.pathname.match(/^\/(?:embed|shorts|live|v)\/([A-Za-z0-9_-]{6,15})/);
    if (m && VIDEO_ID_RE.test(m[1])) return m[1];
    return null;
  } catch { return null; }
}

export function youtubeSearchQuery(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (!YOUTUBE_HOSTS.has(parsed.hostname.toLowerCase())) return null;
    if (parsed.pathname !== '/results') return null;
    return parsed.searchParams.get('search_query') || parsed.searchParams.get('q') || '';
  } catch { return null; }
}

export function isYouTubeHubUrl(url: string): boolean {
  if (!isYouTubeHost(url)) return false;
  if (youtubeVideoId(url)) return false;
  try {
    const parsed = new URL(url);
    if (parsed.pathname === '/' || parsed.pathname === '') return true;
    if (parsed.pathname === '/results') return true;
    if (/^\/feed\//.test(parsed.pathname)) return true;
    return false;
  } catch { return false; }
}

export function youtubeEmbedUrl(videoId: string): string {
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?autoplay=1&modestbranding=1&rel=0`;
}

export function crazyGameSlug(url: string): string | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    if (host !== 'crazygames.com' && host !== 'www.crazygames.com') return null;
    const m = parsed.pathname.match(/^\/(?:[a-z]{2}\/)?game\/([a-z0-9-]+)\/?$/i);
    return m ? m[1] : null;
  } catch { return null; }
}

export function crazyGameEmbedUrl(slug: string): string {
  return `https://www.crazygames.com/embed/${encodeURIComponent(slug)}`;
}

export function shouldUseYouTubeCompanion(url: string, mode: RenderMode | undefined): boolean {
  if (mode && mode !== 'smart') return false;
  if (!isYouTubeHost(url)) return false;
  return !!youtubeVideoId(url) || isYouTubeHubUrl(url);
}

export function shouldUseCrazyGameCompanion(_url: string, _mode: RenderMode | undefined): boolean {
  // The companion's auto-embed is unreliable because the CrazyGames bootstrap loader
  // origin-checks its parent against www.crazygames.com. Smart mode now routes CrazyGames
  // game URLs to Native Launch instead (see effectiveRenderMode). Kept as `false` so
  // BrowserPane no longer mounts the companion.
  return false;
}

export function shouldUseGameCompatibility(url: string): boolean {
  const host = hostnameFor(url);
  if (!host) return false;
  return GAME_COMPAT_HOSTS.some((domain) => host === domain || host.endsWith(`.${domain}`));
}

export function shouldUseNativeGameLaunch(url: string): boolean {
  const host = hostnameFor(url);
  const path = pathnameFor(url);
  if (!host) return false;

  if ((host === 'crazygames.com' || host === 'www.crazygames.com') && /^\/(?:[a-z]{2}\/)?game\//.test(path)) return true;
  if (host === 'poki.com' || host.endsWith('.poki.com')) return path.length > 1;
  if (host === 'hypackel.com' || host.endsWith('.hypackel.com')) return true;
  if (host === 'hypackellite.github.io' || host === 'hypackel.github.io') return true;

  return false;
}

export function shouldUseDirectMode(url: string): boolean {
  const host = hostnameFor(url);
  if (!host) return false;
  return SMART_DIRECT_HOSTS.some((domain) => host === domain || host.endsWith(`.${domain}`));
}

export function effectiveRenderMode(url: string, mode: RenderMode | undefined, defaultMode: RenderMode = 'smart'): 'native' | 'direct' | 'proxy' {
  const chosen = mode || defaultMode;
  if (chosen === 'native') return shouldUseNativeGameLaunch(url) ? 'native' : (shouldUseDirectMode(url) ? 'direct' : 'proxy');
  if (chosen === 'direct') return 'direct';
  if (chosen === 'proxy') return 'proxy';
  // Smart mode: game portal pages are unreliable inside any iframe-based proxy,
  // so default them to Native Launch (open as a real top-level browser tab).
  if (shouldUseNativeGameLaunch(url)) return 'native';
  return shouldUseDirectMode(url) ? 'direct' : 'proxy';
}

export function nextRenderMode(current?: RenderMode, url?: string): RenderMode {
  const isGame = url ? shouldUseGameCompatibility(url) : false;
  if (!current || current === 'smart') return isGame ? 'native' : 'direct';
  if (current === 'native') return 'proxy';
  if (current === 'proxy') return 'direct';
  return 'smart';
}
